import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import FullLogo from '../assets/FullLogo';
import { openNotification } from '../utils/UI/openNotification';

interface TwoFactorAuthProps {
  email?: string;
  onSuccess?: () => void;
}

function TwoFactorAuth() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { email } = useLocation().state;

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const {error, message}=await authService.verify2FA(email, code);
      if (error) {
        throw new Error(message);
      }
      openNotification("success", {
        message: "Successfully logged in.",
      }); 
      localStorage.setItem('2FAVerified', 'true');
      navigate('/dashboard/overview');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Verification failed');
      openNotification("error", {
        message: err || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <div className="flex justify-center items-center mb-[60px]">
        <FullLogo width={"322"} height={"64"} />
      </div>
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2">Two-Factor Authentication</h2>
          <p className="text-gray-600">
            Please enter the 2FA code sent to your authenticator app for the email:
            <br />
            <span className="text-black font-bold">{email}</span>
          </p>
        </div>

        <form onSubmit={handleVerify} className="space-y-6">
          <div>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter 2FA code"
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              required
            />
          </div>

          {error && (
            <div className="text-red-500 text-sm text-center">{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-yellow text-white rounded-md hover:bg-dark-yellow focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
          >
            {loading ? 'Verifying...' : 'Verify 2FA'}
          </button>

          <div className="text-center flex justify-between">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="text-blue-500 hover:text-blue-600 text-sm"
            >
              Back to Login
            </button>
            <button
              type="button"
              onClick={() => navigate('/setup2fa', { state: { email } })}
              className="text-blue-500 hover:text-blue-600 text-sm"
            >
              Setup 2FA Authentication
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TwoFactorAuth; 